import { Component } from '@theme/component';

/**
 * @typedef {Object} ExpandableRefs
 * @property {HTMLElement} content - The content wrapper that gets clamped and measured
 * @property {HTMLButtonElement} button - The toggle button
 * @property {HTMLElement} [label] - The button label element (text swaps on expand/collapse)
 */

/**
 * A custom element that clamps its content to a configurable height and reveals a fade
 * gradient + toggle button only when the content actually overflows that height.
 *
 * The clamp height itself is handled in CSS (responsive via a media query), so this
 * component never reads the pixel values — it compares scrollHeight vs clientHeight.
 *
 * @extends {Component<ExpandableRefs>}
 */
class ExpandableBlock extends Component {
  requiredRefs = ['content', 'button'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @constant {number} */
  #animationSpeed = 300;

  get #allowCollapse() {
    return this.dataset.allowCollapse === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#evaluate());
      this.#resizeObserver.observe(this.refs.content);
    } else {
      window.addEventListener('resize', this.#evaluate);
    }

    this.#evaluate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#evaluate);
  }

  /**
   * Determines whether the content overflows the clamped height and toggles the
   * `is-overflowing` class, which reveals/hides the fade + button in CSS.
   * Skipped while expanded so measuring doesn't fight the open state.
   */
  #evaluate = () => {
    if (this.#expanded) return;

    const { content } = this.refs;
    const overflowing = content.scrollHeight > content.clientHeight + 1;
    this.classList.toggle('is-overflowing', overflowing);
  };

  /**
   * Animates the content's max-height between two values.
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} [onFinish]
   */
  #animateHeight = (startHeight, endHeight, onFinish) => {
    const { content } = this.refs;

    content.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = content.animate(
      { maxHeight: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => onFinish?.();
  };

  /**
   * Toggles between the clamped and fully-expanded states.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    const { content, button, label } = this.refs;

    if (!this.#expanded) {
      const startHeight = content.clientHeight;
      const endHeight = content.scrollHeight;

      this.#expanded = true;
      this.classList.add('is-expanded');
      button.setAttribute('aria-expanded', 'true');

      this.#animateHeight(startHeight, endHeight, () => {
        // Let the content grow freely after the transition.
        content.style.maxHeight = 'none';
        content.style.overflow = 'visible';
      });

      if (this.#allowCollapse) {
        if (label && this.dataset.collapseLabel) label.textContent = this.dataset.collapseLabel;
      } else {
        // One-way expand: drop the overflow state so CSS hides the button (and fade)
        // for good. `hidden` alone wouldn't win against the `is-overflowing` rule.
        this.classList.remove('is-overflowing');
      }
    } else {
      const startHeight = content.scrollHeight;

      // Drop the expanded state FIRST so the CSS clamp defines the collapsed height,
      // then measure. (Measuring while `is-expanded` was applied returned the full
      // height, so the animation had nothing to travel and it snapped shut.)
      this.#expanded = false;
      this.classList.remove('is-expanded');
      content.style.overflow = 'hidden';
      content.style.maxHeight = '';
      const endHeight = content.clientHeight;
      content.style.maxHeight = `${startHeight}px`;

      button.setAttribute('aria-expanded', 'false');

      this.#animateHeight(startHeight, endHeight, () => {
        // Hand control back to the CSS clamp.
        content.style.maxHeight = '';
        content.style.overflow = '';
        if (label && this.dataset.expandLabel) label.textContent = this.dataset.expandLabel;
        this.#evaluate();
      });
    }
  };
}

if (!customElements.get('expandable-block')) {
  customElements.define('expandable-block', ExpandableBlock);
}

export default ExpandableBlock;
